import React from 'react';
import { adminStyles } from '../../styles/admin';
import { BarChart3, Users, QrCode, ShoppingBag } from 'lucide-react';
import { SalesData, QRScan } from '../../types/analytics';

interface DashboardOverviewProps {
  salesData: SalesData | null;
  loading: boolean;
  /** Real 24h unique visitors, fetched with getTotalUniqueVisitors(24).
   *  salesData.uniqueVisitors is NOT used because adminService computes it
   *  with getUniqueVisitors('24'), which treats '24' as a table number and
   *  always returns ~0. */
  uniqueVisitors24h: number | null;
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ salesData, loading, uniqueVisitors24h }) => {
  if (loading) {
    // Shimmer cards instead of a bare spinner — the admin aggregations hit
    // several RTDB nodes, so this routinely takes a moment on slow networks.
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
        role="status"
        aria-label="Loading dashboard"
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className={adminStyles.components.card.base}>
            <div className="p-4 sm:p-5 space-y-3 animate-pulse">
              <div className="h-4 bg-stone-200 rounded w-24" />
              <div className="h-8 bg-stone-200 rounded w-16" />
              <div className="h-3 bg-stone-200 rounded w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!salesData) {
    return <div>No data available</div>;
  }

  // adminService returns today's orders oldest-first (slice of an ascending
  // timestamp query); surface the newest ones instead.
  const recentOrders = [...(salesData.recentOrders ?? [])]
    .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
    .slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Sales Card */}
        <div className={adminStyles.components.card.base}>
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total Sales (7 days)</p>
                <p className="text-xl sm:text-2xl font-bold text-stone-800">{salesData.totalSales.toFixed(2)} DH</p>
                <p className="text-xs text-stone-400 mt-1">Via WhatsApp orders</p>
              </div>
              <div className="p-2.5 bg-green-100 rounded-full">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Count Card */}
        <div className={adminStyles.components.card.base}>
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Orders Today</p>
                <p className="text-xl sm:text-2xl font-bold text-stone-800">{salesData.ordersCount}</p>
                <p className="text-xs text-stone-400 mt-1">Total orders processed</p>
              </div>
              <div className="p-2.5 bg-orange-100 rounded-full">
                <ShoppingBag className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Total QR Scans */}
        <div className={adminStyles.components.card.base}>
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">QR Scans (7 days)</p>
                <p className="text-xl sm:text-2xl font-bold text-stone-800">
                  {salesData.qrScans.reduce((acc: number, scan: QRScan) => acc + scan.count, 0)}
                </p>
                <p className="text-xs text-stone-400 mt-1">&nbsp;</p>
              </div>
              <div className="p-2.5 bg-blue-100 rounded-full">
                <QrCode className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Unique Visitors Card */}
        <div className={adminStyles.components.card.base}>
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Unique Visitors (24h)</p>
                <p className="text-xl sm:text-2xl font-bold text-stone-800">
                  {uniqueVisitors24h ?? 0}
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  From QR code scans
                </p>
              </div>
              <div className="p-2.5 bg-purple-100 rounded-full">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Popular Items */}
        <div className={adminStyles.components.card.base}>
          <div className={adminStyles.components.card.header}>
            <h3 className={adminStyles.typography.heading.h3}>Popular Items (7 days)</h3>
          </div>
          <div className={adminStyles.components.card.content}>
            {salesData.popularItems.length === 0 ? (
              <p className="text-stone-500 text-sm">No orders in the last 7 days yet.</p>
            ) : (
              <div className="space-y-2.5">
                {salesData.popularItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-800">{item.name}</span>
                    <span className="text-sm text-stone-500">{item.count} orders</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* QR Scan Details */}
        <div className={adminStyles.components.card.base}>
          <div className={adminStyles.components.card.header}>
            <h3 className={adminStyles.typography.heading.h3}>QR Scan Activity</h3>
          </div>
          <div className={adminStyles.components.card.content}>
            {salesData.qrScans.length === 0 ? (
              <p className="text-stone-500 text-sm">No QR scans in the last 7 days yet.</p>
            ) : (
              <div className="space-y-2.5">
                {salesData.qrScans.map((scan: QRScan, index: number) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-800">Table {scan.tableId}</span>
                    <span className="text-sm text-stone-500">{scan.count} scans</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders (today's orders, newest first) */}
      <div className={adminStyles.components.card.base}>
        <div className={adminStyles.components.card.header}>
          <h3 className={adminStyles.typography.heading.h3}>Recent Orders</h3>
        </div>
        <div className={adminStyles.components.card.content}>
          {recentOrders.length === 0 ? (
            <p className="text-stone-500 text-sm">No orders yet today.</p>
          ) : (
            <div className="space-y-2.5">
              {recentOrders.map((order, index) => (
                <div key={order.id ?? index} className="flex items-center justify-between gap-4 border-b border-stone-100 last:border-0 pb-2.5 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 overflow-hidden text-ellipsis whitespace-nowrap">
                      {order.customerInfo?.name ||
                        (order.tableInfo ? `Table ${order.tableInfo.tableNumber}` : 'Commande')}
                    </p>
                    <p className="text-xs text-stone-500">
                      {new Date(order.timestamp).toLocaleString()} ·{' '}
                      {order.items?.length ?? 0} article(s) ·{' '}
                      {order.deliveryType === 'livraison' ? 'Livraison' : 'Sur place'}
                    </p>
                  </div>
                  <span className="text-sm text-stone-700 font-semibold whitespace-nowrap">
                    {Number(order.total ?? 0).toFixed(2)} DH
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;